import { EMPTY_FIELD } from 'constants/emptyField';
import { Resources } from 'constants/resources';

import { usePermissions } from 'hooks/usePermissions';
import { StyledCard } from 'pages/Orders/OrdersShow/OrdersShowCommonInfo/OrdersShowCommonInfo.styled';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { RecordContextProvider, useGetOne, useShowController } from 'react-admin';
import { Box } from 'shared/mui/Box';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { CardTable } from 'shared/mui/Card/CardTableNew/CardTable';
import { TableRowWrapper } from 'shared/mui/Table/TableRowWrapperNew/TableRowWrapper';
import { Typography } from 'shared/mui/Typography';
import { FunctionField, TextField } from 'shared/react-admin/Fields';
import { ReferenceField } from 'shared/react-admin/ReferenceFields';
import { OrderStatusBackgroundColorKey } from 'themes/statusColors';
import { Method } from 'types/embedded/method';
import { Order } from 'types/embedded/order';
import { OrderStatus } from 'types/embedded/orderStatus';

import { OrdersShowCommonInfoStateAdjustmentsField } from './OrdersShowCommonInfoStateAdjustmentsField';
import { OrdersShowCommonInfoStateCardHeader } from './OrdersShowCommonInfoStateCardHeader/OrdersShowCommonInfoStateCardHeader';
import { OrdersShowCommonInfoStateDateTimeField } from './OrdersShowCommonInfoStateDateTimeField';
import { OrdersImageFieldStyled } from '../../../components/OrdersImageField';

export const OrdersShowCommonInfoState = (): JSX.Element => {
  const translate = useTypedTranslate();

  const { hasResourceAccess } = usePermissions();
  const { record: order } = useShowController<Order>();

  const { data: status } = useGetOne<OrderStatus>(
    'new/order_statuses',
    { id: order?.status?.['@id'] || '' },
    { enabled: !!order },
  );

  return (
    <RecordContextProvider value={order}>
      <StyledCard
        sx={(theme) => ({
          backgroundColor: status
            ? theme.palette.status.orderStatusBackground[
                status.value as OrderStatusBackgroundColorKey
              ]
            : 'initial',
        })}
      >
        <OrdersShowCommonInfoStateCardHeader />
        <CardContent sx={{ padding: '0 16px 16px !important' }}>
          <CardTable
            sx={{ backgroundColor: '#fff' }}
            tableBodyProps={{ sx: { '& td': { width: '160px' } } }}
          >
            <TableRowWrapper label={translate('app.orders.amountAdjustment')}>
              <OrdersShowCommonInfoStateAdjustmentsField />
            </TableRowWrapper>
            <TableRowWrapper label={translate('app.table.direction')}>
              <ReferenceField
                label={translate('app.table.direction')}
                reference="directions"
                source="direction"
              >
                <TextField source="description" />
              </ReferenceField>
            </TableRowWrapper>
            <TableRowWrapper label={translate('app.table.method')}>
              <ReferenceField
                emptyText={EMPTY_FIELD}
                link={false}
                reference="methods"
                source="method.@id"
              >
                <FunctionField
                  render={(record: Method) => (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {hasResourceAccess(Resources.MethodLogos) && record?.logo && (
                        <OrdersImageFieldStyled source="logo.filePath" />
                      )}
                      <Typography variant="body2">{record?.name}</Typography>
                    </Box>
                  )}
                />
              </ReferenceField>
            </TableRowWrapper>
            <TableRowWrapper label={translate('app.common.updated')}>
              <OrdersShowCommonInfoStateDateTimeField />
            </TableRowWrapper>
          </CardTable>
        </CardContent>
      </StyledCard>
    </RecordContextProvider>
  );
};
